import { AnimationEvent, CSSProperties } from 'react'

import { BADGE_TIER_STYLES } from '../../../common/badge-art/index.js'
import { PlaytimeBadgeArt } from '../../../common/types/playtime.js'
import { BadgeArt, spinBadgeArtWithin } from '../badge-art/badge-art.js'
import './badge-reveal-stage.scss'

export type RevealPhase = 'locked' | 'revealing' | 'done'

export interface BadgeRevealStageProps {
    art: PlaytimeBadgeArt
    idPrefix: string
    phase: RevealPhase
    /** e.g. "+350 punkti" */
    pointsLabel: string
    /** Fired when the opening animation has finished (the view then marks the badge seen). */
    onRevealEnd: () => void
}

const SPARK_COUNT = 22
/** Stage width the burst distances are scaled to (the modal stage is min(300px, 62vw)). */
const STAGE_PX = 300

/** Deterministic spread so the burst looks the same every time (no Math.random in render). */
const sparks = (tier: PlaytimeBadgeArt['tier']) => {
    const colors = BADGE_TIER_STYLES[tier].spark
    return Array.from({ length: SPARK_COUNT }, (_, i) => {
        const angle = (i * 137.5 * Math.PI) / 180
        const dist = 0.42 + (((i * 7) % 10) / 10) * 0.3
        const size = 0.5 + (((i * 3) % 9) / 9) * 0.9
        return {
            color: colors[i % colors.length],
            dx: Math.cos(angle) * dist * STAGE_PX,
            dy: Math.sin(angle) * dist * STAGE_PX,
            size,
            delay: 120 + (i % 5) * 60,
        }
    })
}

export const BadgeRevealStage = (props: BadgeRevealStageProps) => {
    if (props.phase === 'done') {
        return (
            <span className="badge-reveal-stage">
                <BadgeArt
                    art={props.art}
                    state="active"
                    idPrefix={`${props.idPrefix}-gold`}
                    tilt="self"
                />
            </span>
        )
    }
    const revealing = props.phase === 'revealing'
    const handleAnimationEnd = (e: AnimationEvent<HTMLSpanElement>) => {
        if (e.animationName !== 'badge-reveal-open') return
        // Prototype: the opened badge spins once (≈1.1 s) before settling.
        void spinBadgeArtWithin(e.currentTarget).then(props.onRevealEnd)
    }
    return (
        <span className={`badge-reveal-stage${revealing ? ' badge-reveal-stage--revealing' : ''}`}>
            <span className="badge-reveal-stage__layer badge-reveal-stage__lock">
                <BadgeArt art={props.art} state="slot" idPrefix={`${props.idPrefix}-lock`} />
            </span>
            {revealing && (
                <>
                    <span
                        className="badge-reveal-stage__layer badge-reveal-stage__gold"
                        onAnimationEnd={handleAnimationEnd}
                    >
                        <BadgeArt
                            art={props.art}
                            state="active"
                            idPrefix={`${props.idPrefix}-gold`}
                        />
                    </span>
                    <span className="badge-reveal-stage__flash" />
                    {sparks(props.art.tier).map((s, i) => (
                        <span
                            key={i}
                            className="badge-reveal-stage__spark"
                            style={
                                {
                                    background: s.color,
                                    '--dx': `${s.dx.toFixed(0)}px`,
                                    '--dy': `${s.dy.toFixed(0)}px`,
                                    '--sz': s.size.toFixed(2),
                                    animationDelay: `${560 + s.delay}ms`,
                                } as CSSProperties
                            }
                        />
                    ))}
                    <span className="badge-reveal-stage__pop">{props.pointsLabel}</span>
                </>
            )}
        </span>
    )
}
